import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/layout/Layout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { getComparison, ComparisonRow } from '../api/franchise.api';
import styles from './ComparisonPage.module.css';

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function getDefaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: formatDate(from), to: formatDate(to) };
}

export default function ComparisonPage() {
  const { franchiseId } = useAuth();
  const defaultRange = useMemo(getDefaultRange, []);
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [rows, setRows] = useState<ComparisonRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!franchiseId) return;
    setLoading(true);
    (async () => {
      try {
        const data = await getComparison(franchiseId, from, to);
        setRows(data);
        setError('');
      } catch (err) {
        setError(err instanceof Error ? err.message : '데이터 조회 실패');
      } finally {
        setLoading(false);
      }
    })();
  }, [franchiseId, from, to]);

  const maxWaiting = useMemo(() => {
    if (rows.length === 0) return 1;
    return Math.max(...rows.map((r) => r.totalWaiting), 1);
  }, [rows]);

  const bestBranch = useMemo(() => {
    if (rows.length === 0) return null;
    // Best = lowest noShowRate among branches with data
    return rows.reduce((best, r) => {
      if (r.totalWaiting === 0) return best;
      if (!best) return r;
      return r.noShowRate < best.noShowRate ? r : best;
    }, null as ComparisonRow | null);
  }, [rows]);

  return (
    <Layout title="성과 비교">
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>시작일</label>
          <input
            type="date"
            className={styles.dateInput}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>종료일</label>
          <input
            type="date"
            className={styles.dateInput}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className={styles.error}>{error}</div>
      ) : (
        <>
          {bestBranch && (
            <div className={styles.bestBanner}>
              최우수 지점: <strong>{bestBranch.storeName}</strong> (노쇼율{' '}
              {bestBranch.noShowRate.toFixed(1)}%)
            </div>
          )}

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>지점명</th>
                  <th>총 대기</th>
                  <th>노쇼율</th>
                  <th>평균 대기시간</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className={styles.empty}>
                      데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const isBest = bestBranch?.storeId === r.storeId;
                    return (
                      <tr key={r.storeId} className={isBest ? styles.bestRow : undefined}>
                        <td className={styles.name}>
                          {r.storeName}
                          {isBest && <span className={styles.bestBadge}>BEST</span>}
                        </td>
                        <td>{r.totalWaiting}</td>
                        <td>{r.noShowRate.toFixed(1)}%</td>
                        <td>{r.avgWaitMinutes.toFixed(0)}분</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {rows.length > 0 && (
            <>
              <h2 className={styles.chartTitle}>지점별 대기건수</h2>
              <div className={styles.chart}>
                {rows.map((r) => {
                  const pct = (r.totalWaiting / maxWaiting) * 100;
                  const isBest = bestBranch?.storeId === r.storeId;
                  return (
                    <div key={r.storeId} className={styles.chartRow}>
                      <div className={styles.chartLabel}>{r.storeName}</div>
                      <div className={styles.chartBarBg}>
                        <div
                          className={`${styles.chartBar} ${isBest ? styles.chartBarBest : ''}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className={styles.chartValue}>{r.totalWaiting}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </Layout>
  );
}
